if (!customElements.get('main-product')) {
  class MainProduct extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      this.elForm = this.querySelector('form');
      if (!this.elForm) return;

      this.elVariantInput = this.elForm.querySelector('[name="id"]');
      this.elSellingPlanInput = this.elForm.querySelector('[name="selling_plan"]');
      this.elSubmit = this.elForm.querySelector('[data-submit-btn]');
      this.elSubmitCopy = this.elSubmit?.querySelector('[data-copy]');
      this.elDynamicPrice = this.elSubmit?.querySelector('[data-dynamic-price]');
      this.elSubmitIcon = this.elSubmit?.querySelector('[data-submit-icon]');
      this.elOnetimePrice = this.querySelector('[data-onetime-price]');
      this.elBuyOnce = this.querySelector('[data-buy-once]');
      this.elPlanCards = this.querySelectorAll('[data-plan-card]');
      this.elAddon = this.querySelector('[data-addon]');
      this.elAddonInput = this.querySelector('[data-addon-input]');

      this.elVariantsJson = this.querySelector('[data-variants]');
      if (!this.elVariantsJson) return;

      try {
        this.variants = JSON.parse(this.elVariantsJson.innerHTML);
      } catch (error) {
        console.error('[MP] Could not parse variant JSON:', error);
        return;
      }

      var elGiftJson = this.querySelector('[data-variant-gifts]');

      try {
        this.giftData = elGiftJson
          ? JSON.parse(elGiftJson.innerHTML)
          : [];
      } catch (error) {
        console.error('[MP] Could not parse gift JSON:', error);
        this.giftData = [];
      }

      this.elGiftCallout = this.querySelector('[data-gift-callout]');
      this.elGiftHeading = this.querySelector('[data-gift-callout-heading]');
      this.elGiftTitle = this.querySelector('[data-gift-callout-title]');
      this.elGiftDescription = this.querySelector('[data-gift-callout-description]');

      this.currentVariant = this.variants.find(
        v => v.id == this.elVariantInput.value
      );

      if (!this.currentVariant) {
        console.error('[MP] Initial variant was not found.');
        return;
      }

      this.currentOptions = { ...this.currentVariant.options };
      this.currentSubscription = null;
      this.hasSubscription = false;

      console.group('[MP] INIT — All variant data');

      console.table(
        this.variants.map(v => ({
          id: v.id,
          options: JSON.stringify(v.options),
          available: v.available,
          onetime_only: v.onetime_only,
          selling_plans:
            (v.selling_plans || [])
              .map(sp => sp.id + ' (' + sp.name + ')')
              .join(', ') || 'NONE'
        }))
      );

      console.log(
        '[MP] Initial variant:',
        this.currentVariant.id,
        this.currentVariant.options
      );

      console.groupEnd();

      this.packSizeOptionKey = this._findPackSizeOptionKey();
      this.formatOptionKey = this._findFormatOptionKey();

      const checkedFormat = this.elForm.querySelector(
        '[data-format-servings]:checked'
      );

      this.servingsPerUnit = checkedFormat
        ? parseInt(checkedFormat.dataset.formatServings) || 60
        : 60;

      this.elForm.addEventListener(
        'change',
        this._onFormChange.bind(this)
      );

      this.elForm.addEventListener(
        'submit',
        this._addToCart.bind(this)
      );

      if (this.elBuyOnce) {
        this.elBuyOnce.addEventListener(
          'click',
          this._buyOnce.bind(this)
        );
      }

      var flavorKey = Object.keys(this.currentOptions).find(
        k =>
          k !== this.formatOptionKey &&
          k !== this.packSizeOptionKey
      );

      if (flavorKey) {
        var checkedFlavor = this.elForm.querySelector(
          'input[name="' + flavorKey + '"]:checked'
        );

        if (
          checkedFlavor &&
          this.currentOptions[flavorKey] !== checkedFlavor.value
        ) {
          this.currentOptions[flavorKey] = checkedFlavor.value;

          var resolved = this.variants.find(v =>
            JSON.stringify(v.options) ===
            JSON.stringify(this.currentOptions)
          );

          if (resolved) {
            this.currentVariant = resolved;
            this.elVariantInput.value = resolved.id;
          }
        }
      }

      this._updateFormatAvailability();
      this._preselectBestPlan();
      this._updatePlanAvailability();
      this._updateAllPlanPrices();
      this._updateOnetimePrice();
      this._updateSelectedPlanVisuals();

      this._initVideoControls();
      this._initFlavorCarousel();
      this._waitForGsapCarousel();
      this._initLightbox();
      this._initDosingGuide();
      this._setupResizeVideoThrottle();
      this._updateGalleryImage();
      this._updateGiftCallout();
      this._updateAddonVisibility();

      this.querySelectorAll('[data-gallery-video]').forEach(
        v => {
          v.play().catch(() => {});
        }
      );

      this._scrollToHashTarget();

      this.initialized = true;
    }

    disconnectedCallback() {
      if (this._carouselRO) {
        this._carouselRO.disconnect();
        this._carouselRO = null;
      }

      if (this._resizeHandler) {
        window.removeEventListener(
          'resize',
          this._resizeHandler
        );

        this._resizeHandler = null;
      }

      if (this._fetchAbort) {
        this._fetchAbort.abort();
        this._fetchAbort = null;
      }
    }

    _initFlavorCarousel() {
      this.flavorTrack =
        this.querySelector('[data-flavor-track]');

      this.flavorViewport =
        this.querySelector('[data-flavor-viewport]');

      this.flavorDotsContainer =
        this.querySelector('[data-flavor-dots]');

      this.flavorDotsWrapper =
        this.querySelector('[data-flavor-dots-wrapper]');

      if (
        !this.flavorTrack ||
        !this.flavorViewport
      ) {
        return;
      }

      this.flavorItems = [
        ...this.flavorTrack.querySelectorAll('label')
      ];

      if (
        this.flavorItems.length <= 1 ||
        window.innerWidth >= 768
      ) {
        return;
      }

      this._waitFlavorGsap();
    }

    _waitFlavorGsap() {
      if (
        typeof gsap !== 'undefined' &&
        typeof Observer !== 'undefined'
      ) {
        this._setupFlavorGsap();
      } else {
        requestAnimationFrame(() =>
          this._waitFlavorGsap()
        );
      }
    }

    _setupFlavorGsap() {
      gsap.registerPlugin(Observer);

      const vw = window.innerWidth;
      const items = this.flavorItems;
      const gap = 8;
      const padL = 12;
      const padR = 12;
      const itemW = (vw - 28) * 3 / 7;
      const step = itemW + gap;
      const totalW =
        padL +
        items.length * itemW +
        (items.length - 1) * gap +
        padR;

      const viewW = vw;
      const maxShift = totalW - viewW;

      if (maxShift <= 0) return;

      this._flavorPositions = [0];

      let pos = step;

      while (pos < maxShift) {
        this._flavorPositions.push(pos);
        pos += step;
      }

      if (
        this._flavorPositions[
          this._flavorPositions.length - 1
        ] < maxShift
      ) {
        this._flavorPositions.push(maxShift);
      }

      this._flavorCurrent = 0;
      this._flavorAnimating = false;

      this._buildFlavorDots();

      let startX = 0;
      let startY = 0;
      let axis = null;

      this.flavorViewport.addEventListener(
        'touchstart',
        e => {
          startX = e.changedTouches[0].clientX;
          startY = e.changedTouches[0].clientY;
          axis = null;
        },
        { passive: true }
      );

      this.flavorViewport.addEventListener(
        'touchmove',
        e => {
          if (axis) return;

          const dx = Math.abs(
            e.changedTouches[0].clientX - startX
          );

          const dy = Math.abs(
            e.changedTouches[0].clientY - startY
          );

          if (dx + dy > 20) {
            axis = dx > dy * 2 ? 'x' : 'y';
          }
        },
        { passive: true }
      );

      this.flavorViewport.addEventListener(
        'touchend',
        e => {
          if (axis !== 'x') return;

          const dx =
            e.changedTouches[0].clientX - startX;

          if (Math.abs(dx) > 40) {
            this._goFlavor(
              dx < 0 ? 1 : -1
            );
          }
        },
        { passive: true }
      );
    }

    _goFlavor(dir) {
      if (this._flavorAnimating) return;

      const next =
        this._flavorCurrent + dir;

      if (
        next < 0 ||
        next >= this._flavorPositions.length
      ) {
        return;
      }

      this._flavorAnimating = true;
      this._flavorCurrent = next;

      gsap.to(this.flavorTrack, {
        x: -this._flavorPositions[next],
        duration: 0.4,
        ease: 'power2.out',
        onComplete: () => {
          this._flavorAnimating = false;
        }
      });

      this._updateFlavorDots();
    }

    _buildFlavorDots() {
      if (
        !this.flavorDotsContainer ||
        this._flavorPositions.length <= 1
      ) {
        return;
      }

      this.flavorDotsContainer.innerHTML = '';

      for (
        let i = 0;
        i < this._flavorPositions.length;
        i++
      ) {
        const dot =
          document.createElement('span');

        dot.className =
          'h-1 rounded-full cursor-pointer';

        dot.style.width =
          i === 0 ? '20px' : '4px';

        dot.style.backgroundColor =
          i === 0
            ? '#BE4300'
            : 'rgba(190, 67, 0, 0.4)';

        dot.dataset.flavorDot = i;

        dot.addEventListener(
          'click',
          () => {
            if (
              this._flavorAnimating ||
              i === this._flavorCurrent
            ) {
              return;
            }

            this._flavorAnimating = true;
            this._flavorCurrent = i;

            gsap.to(this.flavorTrack, {
              x: -this._flavorPositions[i],
              duration: 0.4,
              ease: 'power2.out',
              onComplete: () => {
                this._flavorAnimating = false;
              }
            });

            this._updateFlavorDots();
          }
        );

        this.flavorDotsContainer.appendChild(dot);
      }

      this.flavorDots = [
        ...this.flavorDotsContainer.querySelectorAll(
          '[data-flavor-dot]'
        )
      ];

      if (this.flavorDotsWrapper) {
        this.flavorDotsWrapper.style.display = '';
      }
    }

    _updateFlavorDots() {
      if (
        !this.flavorDots ||
        !this.flavorDots.length
      ) {
        return;
      }

      this.flavorDots.forEach(
        (dot, i) => {
          const isActive =
            i === this._flavorCurrent;

          gsap.to(dot, {
            width: isActive ? 20 : 4,
            backgroundColor: isActive
              ? '#BE4300'
              : 'rgba(190, 67, 0, 0.4)',
            duration: 0.35,
            ease: 'power2.out'
          });
        }
      );
    }

    _initVideoControls() {
      this.querySelectorAll(
        '[data-video-controls]'
      ).forEach(controls => {
        const container =
          controls.closest('figure') ||
          controls.parentElement;

        const video =
          container.querySelector(
            '[data-gallery-video]'
          );

        if (!video) return;

        const muteBtn =
          controls.querySelector(
            '[data-mute-toggle]'
          );

        const playBtn =
          controls.querySelector(
            '[data-play-toggle]'
          );

        if (muteBtn) {
          muteBtn.addEventListener(
            'click',
            () => {
              video.muted = !video.muted;

              muteBtn
                .querySelector('[data-icon-muted]')
                ?.classList.toggle(
                  'hidden',
                  !video.muted
                );

              muteBtn
                .querySelector('[data-icon-unmuted]')
                ?.classList.toggle(
                  'hidden',
                  video.muted
                );
            }
          );
        }

        if (playBtn) {
          playBtn.addEventListener(
            'click',
            async () => {
              try {
                if (video.paused) {
                  await video.play();
                } else {
                  video.pause();
                }
              } catch (error) {
                console.warn(
                  '[MP] Video play failed:',
                  error
                );
              }

              playBtn
                .querySelector('[data-icon-pause]')
                ?.classList.toggle(
                  'hidden',
                  video.paused
                );

              playBtn
                .querySelector('[data-icon-play]')
                ?.classList.toggle(
                  'hidden',
                  !video.paused
                );
            }
          );
        }
      });
    }

    _setupResizeVideoThrottle() {
      let tid;

      const pause = () => {
        if (this._galleryVideosPaused) return;

        this._galleryVideosPaused = true;

        this.querySelectorAll(
          '[data-gallery-video]'
        ).forEach(v => {
          if (!v.paused) {
            v.dataset.wasPlaying = '';
            v.pause();
          }
        });

        this.querySelectorAll(
          '[data-video-controls]'
        ).forEach(c => {
          c.style.visibility = 'hidden';
        });
      };

      const resume = () => {
        this._galleryVideosPaused = false;

        this.querySelectorAll(
          '[data-gallery-video]'
        ).forEach(v => {
          if (
            v.dataset.wasPlaying !== undefined
          ) {
            delete v.dataset.wasPlaying;

            v.play().catch(() => {});
          }
        });

        this.querySelectorAll(
          '[data-video-controls]'
        ).forEach(c => {
          c.style.visibility = '';
        });
      };

      this._resizeHandler = () => {
        pause();

        clearTimeout(tid);

        tid = setTimeout(
          resume,
          200
        );
      };

      window.addEventListener(
        'resize',
        this._resizeHandler
      );
    }

    _waitForGsapCarousel() {
      if (
        typeof gsap !== 'undefined' &&
        typeof Observer !== 'undefined'
      ) {
        this._initCarousel();
      } else {
        requestAnimationFrame(() =>
          this._waitForGsapCarousel()
        );
      }
    }

    _initCarousel() {
      gsap.registerPlugin(Observer);

      this.carouselViewport =
        this.querySelector(
          '[data-carousel-viewport]'
        );

      this.slides = [
        ...this.querySelectorAll(
          '[data-carousel-slide]'
        )
      ];

      this.slideCount =
        this.slides.length;

      this.dots = [
        ...this.querySelectorAll(
          '[data-carousel-dot]'
        )
      ];

      this.currentSlide = 0;
      this.carouselAnimating = false;

      if (
        this.slideCount <= 1 ||
        !this.carouselViewport
      ) {
        return;
      }

      this.carouselGap = 8;

      this.slides.forEach(
        (item, i) => {
          gsap.set(
            item,
            this._cProps(
              this._cSlot(i)
            )
          );
        }
      );

      const activeVideo =
        this.slides[
          this.currentSlide
        ]?.querySelector(
          '[data-gallery-video]'
        );

      if (activeVideo) {
        activeVideo.play().catch(() => {});
      }

      let startX = 0;
      let startY = 0;
      let axis = null;

      this.carouselViewport.addEventListener(
        'touchstart',
        e => {
          startX =
            e.changedTouches[0].clientX;

          startY =
            e.changedTouches[0].clientY;

          axis = null;
        },
        { passive: true }
      );

      this.carouselViewport.addEventListener(
        'touchmove',
        e => {
          if (axis) return;

          const dx =
            Math.abs(
              e.changedTouches[0].clientX -
              startX
            );

          const dy =
            Math.abs(
              e.changedTouches[0].clientY -
              startY
            );

          if (dx + dy > 30) {
            axis =
              dx > dy * 2
                ? 'x'
                : 'y';
          }
        },
        { passive: true }
      );

      this.carouselViewport.addEventListener(
        'touchend',
        e => {
          if (axis !== 'x') return;

          const dx =
            e.changedTouches[0].clientX -
            startX;

          if (Math.abs(dx) > 50) {
            this._cGo(
              dx < 0 ? 1 : -1
            );
          }
        },
        { passive: true }
      );

      this.dots.forEach(dot => {
        dot.addEventListener(
          'click',
          () => {
            const target =
              parseInt(
                dot.dataset.carouselDot
              );

            if (
              target !==
              this.currentSlide
            ) {
              this._cGoTo(target);
            }
          }
        );
      });

      this._setupCarouselResize();
    }

    _cSlot(itemIndex) {
      let d =
        itemIndex -
        this.currentSlide;

      const half =
        this.slideCount / 2;

      if (d > half) {
        d -= this.slideCount;
      }

      if (d < -half) {
        d += this.slideCount;
      }

      return d;
    }

    // _getCarouselItemW() {
    //   const vw =
    //     window.innerWidth;

    //   return vw >= 768 && vw < 1024
    //     ? (vw - 16) / 1.5
    //     : vw * 0.9;
    // }
    _getCarouselItemW() {
  const vw = window.innerWidth;

  // Mobile: 330px center slide on 390px screen
  if (vw < 768) {
    return Math.min(330, vw - 60);
  }

  // Keep existing tablet/desktop sizing
  return vw < 1024
    ? (vw - 16) / 1.5
    : vw * 0.9;
}

    // _cX(slot) {
    //   return (
    //     slot *
    //     (
    //       this._getCarouselItemW() +
    //       this.carouselGap
    //     )
    //   );
    // }
    _cX(slot) {
  // Keep an exact 8px gap between neighboring slides
  return slot * (this._getCarouselItemW() + 8);
}

    // _cProps(slot) {
    //   return {
    //     x: this._cX(slot),
    //     xPercent: -50,
    //     zIndex:
    //       slot === 0
    //         ? 3
    //         : Math.max(
    //             0,
    //             3 - Math.abs(slot)
    //           ),
    //     opacity:
    //       Math.abs(slot) <= 2
    //         ? 1
    //         : 0
    //   };
    // }
    _cProps(slot) {
  return {
    x: this._cX(slot),
    xPercent: -50,
    zIndex:
      slot === 0
        ? 3
        : Math.max(0, 3 - Math.abs(slot)),
    opacity: Math.abs(slot) <= 2 ? 1 : 0
  };
}

    _cGo(dir) {
      if (
        this.carouselAnimating ||
        this.slideCount <= 1
      ) {
        return;
      }

      this.carouselAnimating = true;

      const outVideo =
        this.slides[
          this.currentSlide
        ]?.querySelector(
          '[data-gallery-video]'
        );

      if (outVideo) {
        outVideo.pause();
      }

      const oldSlots =
        this.slides.map(
          (_, i) =>
            this._cSlot(i)
        );

      this.currentSlide =
        (
          (
            this.currentSlide +
            dir
          ) %
          this.slideCount +
          this.slideCount
        ) %
        this.slideCount;

      const newSlots =
        this.slides.map(
          (_, i) =>
            this._cSlot(i)
        );

      const dur = 0.5;

      this.slides.forEach(
        (item, i) => {
          gsap.killTweensOf(item);

          const oldSlot =
            oldSlots[i];

          const newSlot =
            newSlots[i];

          const wraps =
            Math.abs(
              newSlot -
              oldSlot
            ) > 1;

          const target =
            this._cProps(
              newSlot
            );

          if (wraps) {
            const tl =
              gsap.timeline();

            tl.to(item, {
              x: this._cX(
                oldSlot - dir
              ),
              xPercent: -50,
              opacity: 0,
              zIndex: 0,
              duration: dur,
              ease: 'power2.inOut'
            });

            tl.set(
              item,
              target
            );
          } else {
            gsap.to(item, {
              ...target,
              duration: dur,
              ease: 'power2.inOut'
            });
          }
        }
      );

      this._updateDots();

      gsap.delayedCall(
        dur,
        () => {
          this.carouselAnimating = false;

          if (
            this._carouselNeedsReposition
          ) {
            this._repositionSlides();
          }

          const inVideo =
            this.slides[
              this.currentSlide
            ]?.querySelector(
              '[data-gallery-video]'
            );

          if (inVideo) {
            inVideo.currentTime = 0;

            inVideo.play().catch(
              () => {}
            );
          }
        }
      );
    }

    _cGoTo(targetIndex) {
      if (
        this.carouselAnimating ||
        targetIndex ===
          this.currentSlide
      ) {
        return;
      }

      this.carouselAnimating = true;

      const outVideo =
        this.slides[
          this.currentSlide
        ]?.querySelector(
          '[data-gallery-video]'
        );

      if (outVideo) {
        outVideo.pause();
      }

      this.currentSlide =
        targetIndex;

      const dur = 0.5;

      this.slides.forEach(
        (item, i) => {
          gsap.killTweensOf(item);

          gsap.to(
            item,
            {
              ...this._cProps(
                this._cSlot(i)
              ),
              duration: dur,
              ease: 'power2.inOut'
            }
          );
        }
      );

      this._updateDots();

      gsap.delayedCall(
        dur,
        () => {
          this.carouselAnimating = false;

          if (
            this._carouselNeedsReposition
          ) {
            this._repositionSlides();
          }

          const inVideo =
            this.slides[
              this.currentSlide
            ]?.querySelector(
              '[data-gallery-video]'
            );

          if (inVideo) {
            inVideo.currentTime = 0;

            inVideo.play().catch(
              () => {}
            );
          }
        }
      );
    }

    _updateDots() {
      this.dots.forEach(
        (dot, i) => {
          const isActive =
            i ===
            this.currentSlide;

          dot.style.width =
            isActive
              ? '20px'
              : '4px';

          dot.style.backgroundColor =
            isActive
              ? '#ffffff'
              : 'rgba(255, 255, 255, 0.4)';
        }
      );
    }

    _updateGalleryImage() {
      const heroes =
        this.querySelectorAll(
          '[data-gallery-hero]'
        );

      heroes.forEach(
        hero => {
          const img =
            hero.querySelector('img');

          if (!img) return;

          if (!hero._metafieldHeroSrc) {
            hero._metafieldHeroSrc =
              img.src;

            hero._metafieldHeroSrcset =
              img.srcset || '';

            hero._metafieldLightboxSrc =
              hero.dataset.lightboxSrc || '';
          }

          img.srcset =
            hero._metafieldHeroSrcset;

          img.src =
            hero._metafieldHeroSrc;

          if (
            hero.dataset.lightboxSrc !==
            undefined
          ) {
            hero.dataset.lightboxSrc =
              hero._metafieldLightboxSrc;
          }
        }
      );
    }

    _setupCarouselResize() {
      let debounceId;

      this._carouselRO =
        new ResizeObserver(
          () => {
            clearTimeout(
              debounceId
            );

            debounceId =
              setTimeout(
                () =>
                  this._repositionSlides(),
                150
              );
          }
        );

      this._carouselRO.observe(
        this.carouselViewport
      );
    }

    _repositionSlides() {
      if (
        !this.carouselViewport ||
        this.slideCount <= 1
      ) {
        return;
      }

      if (
        this.carouselViewport
          .offsetWidth === 0
      ) {
        return;
      }

      if (this.carouselAnimating) {
        this._carouselNeedsReposition =
          true;

        return;
      }

      this._carouselNeedsReposition =
        false;

      this.slides.forEach(
        (item, i) => {
          gsap.set(
            item,
            this._cProps(
              this._cSlot(i)
            )
          );
        }
      );
    }

    _findPackSizeOptionKey() {
      const packSizeValues = [
        'Single Pack',
        'Double Pack',
        'Triple Pack'
      ];

      for (
        const key in this.currentOptions
      ) {
        if (
          packSizeValues.includes(
            this.currentOptions[key]
          )
        ) {
          return key;
        }
      }

      return null;
    }

    _findFormatOptionKey() {
      const formatRadio =
        this.elForm.querySelector(
          '[data-format]'
        );

      return formatRadio
        ? formatRadio.name
        : null;
    }

    _updateFormatAvailability() {
      if (!this.formatOptionKey) {
        return;
      }

      const flavorKey =
        Object.keys(
          this.currentOptions
        ).find(
          k =>
            k !==
              this.formatOptionKey &&
            k !==
              this.packSizeOptionKey
        );

      if (!flavorKey) return;

      const currentFlavor =
        this.currentOptions[
          flavorKey
        ];

      const formatRadios =
        this.elForm.querySelectorAll(
          '[data-format]'
        );

      let currentFormatAvailable =
        false;

      formatRadios.forEach(
        radio => {
          const hasVariants =
            this.variants.some(
              v =>
                v.options[
                  flavorKey
                ] === currentFlavor &&
                v.options[
                  this.formatOptionKey
                ] === radio.value
            );

          const label =
            radio.closest('label');

          radio.disabled =
            !hasVariants;

          if (label) {
            label.classList.toggle(
              'opacity-40',
              !hasVariants
            );

            label.classList.toggle(
              'pointer-events-none',
              !hasVariants
            );

            label.classList.toggle(
              'cursor-pointer',
              hasVariants
            );
          }

          if (
            radio.checked &&
            hasVariants
          ) {
            currentFormatAvailable =
              true;
          }
        }
      );

      if (
        !currentFormatAvailable
      ) {
        const firstAvailable =
          [
            ...formatRadios
          ].find(
            r => !r.disabled
          );

        if (firstAvailable) {
          firstAvailable.checked =
            true;

          this.servingsPerUnit =
            parseInt(
              firstAvailable.dataset
                .formatServings
            ) || 60;

          this.currentOptions[
            this.formatOptionKey
          ] =
            firstAvailable.value;
        }
      }
    }
    
    _getCurrentFlavor() {
      const flavorKey =
        Object.keys(
          this.currentOptions
        ).find(
          key =>
            key !==
              this.formatOptionKey &&
            key !==
              this.packSizeOptionKey
        );

      return flavorKey
        ? this.currentOptions[
            flavorKey
          ]
        : null;
    }

    _getCurrentGift() {
      const currentFlavor =
        this._getCurrentFlavor();

      if (
        !Array.isArray(
          this.giftData
        )
      ) {
        return null;
      }

      return (
        this.giftData.find(
          gift => {
            if (
              gift.product_variant_id ==
              null
            ) {
              return false;
            }

            return (
              gift.flavor == null ||
              gift.flavor ===
                currentFlavor
            );
          }
        ) || null
      );
    }

    _getCurrentGiftVariantId() {
      const gift =
        this._getCurrentGift();

      if (
        !gift?.product_variant_id
      ) {
        return null;
      }

      const id =
        Number(
          gift.product_variant_id
        );

      return Number.isFinite(id)
        ? id
        : null;
    }

    _updateGiftCallout() {
      if (
        !this.elGiftCallout ||
        !this.giftData.length
      ) {
        return;
      }

      const currentFlavor =
        this._getCurrentFlavor();

      const gift =
        this.giftData.find(
          g =>
            g.flavor == null ||
            g.flavor === currentFlavor
        );

      if (gift) {
        if (this.elGiftHeading) {
          this.elGiftHeading.textContent =
            gift.callout_heading || '';
        }

        if (this.elGiftTitle) {
          this.elGiftTitle.textContent =
            gift.callout_title || '';
        }

        if (
          this.elGiftDescription
        ) {
          this.elGiftDescription.textContent =
            gift.callout_description ||
            '';
        }

        this.elGiftCallout.style.display =
          '';
      } else {
        this.elGiftCallout.style.display =
          'none';
      }
    }

    _onFormChange({ target }) {
      if (
        target.dataset
          .formatServings
      ) {
        this.servingsPerUnit =
          parseInt(
            target.dataset
              .formatServings
          ) || 60;
      }

      if (
        target.name ===
        'subscription'
      ) {
        this._setSubscription(
          target.value
        );
      } else if (
        target.name &&
        target.name.includes(
          'option'
        )
      ) {
        this._setVariant(
          target.name,
          target.value
        );
      }
    }

    _setVariant(
      option,
      value
    ) {
      console.group(
        '[MP] _setVariant called: ' +
          option +
          ' = ' +
          value
      );

      console.log(
        '[MP] BEFORE — currentOptions:',
        JSON.stringify(
          this.currentOptions
        )
      );

      console.log(
        '[MP] BEFORE — currentVariant:',
        this.currentVariant.id
      );

      console.log(
        '[MP] BEFORE — currentSubscription:',
        this.currentSubscription
          ? {
              id:
                this.currentSubscription.id,
              name:
                this.currentSubscription.name
            }
          : null
      );

      console.log(
        '[MP] BEFORE — hasSubscription:',
        this.hasSubscription
      );

      console.log(
        '[MP] BEFORE — elSellingPlanInput.value:',
        this.elSellingPlanInput?.value
      );

      this.currentOptions[
        option
      ] = value;

      this._updateFormatAvailability();

      let packSize =
        'Single Pack';

      if (
        this.hasSubscription &&
        this.currentSubscription
      ) {
        const card =
          this.querySelector(
            `[data-selling-plan-id="${this.currentSubscription.id}"]`
          );

        packSize =
          card?.dataset.packSize ||
          'Single Pack';

        console.log(
          '[MP] Pack size from card:',
          packSize,
          '(card found:',
          !!card,
          ')'
        );
      }

      if (
        this.packSizeOptionKey &&
        packSize
      ) {
        const siblings =
          this.variants.filter(
            v =>
              Object.keys(
                this.currentOptions
              ).every(
                k =>
                  k ===
                    this.packSizeOptionKey ||
                  v.options[k] ===
                    this.currentOptions[k]
              )
          );

        const hasPack =
          siblings.some(
            v =>
              v.options[
                this.packSizeOptionKey
              ] === packSize
          );

        this.currentOptions[
          this.packSizeOptionKey
        ] =
          hasPack
            ? packSize
            : (
                siblings[0]
                  ?.options[
                    this.packSizeOptionKey
                  ] ||
                packSize
              );
      }

      console.log(
        '[MP] Options for variant lookup:',
        JSON.stringify(
          this.currentOptions
        )
      );

      const newVariant =
        this.variants.find(
          v =>
            JSON.stringify(
              v.options
            ) ===
            JSON.stringify(
              this.currentOptions
            )
        );

      console.log(
        '[MP] newVariant found:',
        newVariant
          ? newVariant.id
          : 'NULL'
      );

      if (newVariant) {
        console.log(
          '[MP] newVariant.selling_plans:',
          JSON.stringify(
            newVariant.selling_plans ||
              []
          )
        );

        this.currentVariant =
          newVariant;

        this.elVariantInput.value =
          newVariant.id;

        if (
          this.hasSubscription &&
          this.currentSubscription
        ) {
          const plans =
            newVariant.selling_plans ||
            [];

          const idMatch =
            plans.find(
              p =>
                p.id ===
                this.currentSubscription
                  .id
            );

          const nameMatch =
            plans.find(
              p =>
                p.name ===
                this.currentSubscription
                  .name
            );

          const matchingPlan =
            idMatch ||
            nameMatch;

          if (matchingPlan) {
            this.currentSubscription =
              matchingPlan;

            this.elSellingPlanInput.value =
              matchingPlan.id;
          } else if (
            plans.length
          ) {
            this.currentSubscription =
              plans[0];

            this.elSellingPlanInput.value =
              plans[0].id;
          }
        }

        this.elSubmit.disabled =
          !newVariant.available;

        this._updateSubmitButton();
        this._updatePlanAvailability();
        this._updateAllPlanPrices();
        this._updateOnetimePrice();
        this._updateUrl();

        document.dispatchEvent(
          new CustomEvent(
            'variant:change',
            {
              detail: {
                variant: newVariant
              }
            }
          )
        );
        
        this._updateGalleryImage();

        this._fetchSection(
          newVariant.id
        );
      } else {
        console.error(
          '[MP] NO VARIANT FOUND for options:',
          JSON.stringify(
            this.currentOptions
          )
        );
      }

      this._updateGiftCallout();
      this._updateAddonVisibility();

      console.groupEnd();
    }

    _setSubscription(value) {
      this.hasSubscription =
        value !== 'onetime';

      console.log(
        '[MP] _setSubscription:',
        value,
        '| hasSubscription:',
        this.hasSubscription
      );

      if (this.hasSubscription) {
        const sellingPlanId =
          value.replace(
            'subscription-',
            ''
          );

        const allPlans =
          this.currentVariant
            .selling_plans || [];

        this.currentSubscription =
          allPlans.find(
            p =>
              p.id ==
              Number(
                sellingPlanId
              )
          );

        if (
          this.currentSubscription
        ) {
          this.elSellingPlanInput.value =
            this.currentSubscription.id;

          const card =
            this.querySelector(
              `[data-selling-plan-id="${sellingPlanId}"]`
            );

          const packSize =
            card?.dataset.packSize;

          if (packSize) {
            this._updatePackSizeVariant(
              packSize
            );
          }
        }
      } else {
        this.currentSubscription =
          null;

        this.elSellingPlanInput.value =
          '';

        this._updatePackSizeVariant(
          'Single Pack'
        );
      }

      this._updateSelectedPlanVisuals();
      this._updateSubmitButton();
      this._updateUrl();
    }

    _updatePackSizeVariant(
      packSize
    ) {
      if (!this.packSizeOptionKey) {
        return;
      }

      const previousPackSize =
        this.currentOptions[
          this.packSizeOptionKey
        ];

      this.currentOptions[
        this.packSizeOptionKey
      ] = packSize;

      let newVariant =
        this.variants.find(
          v =>
            JSON.stringify(
              v.options
            ) ===
            JSON.stringify(
              this.currentOptions
            )
        );

      if (!newVariant) {
        this.currentOptions[
          this.packSizeOptionKey
        ] =
          previousPackSize;

        newVariant =
          this.variants.find(
            v =>
              JSON.stringify(
                v.options
              ) ===
              JSON.stringify(
                this.currentOptions
              )
          );
      }

      if (
        newVariant &&
        newVariant.id !==
          this.currentVariant.id
      ) {
        this.currentVariant =
          newVariant;

        this.elVariantInput.value =
          newVariant.id;

        if (
          this.hasSubscription &&
          this.currentSubscription
        ) {
          const matchingPlan =
            newVariant.selling_plans?.find(
              plan =>
                plan.name ===
                this.currentSubscription
                  .name
            );

          if (matchingPlan) {
            this.currentSubscription =
              matchingPlan;

            this.elSellingPlanInput.value =
              matchingPlan.id;
          }
        }

        this.elSubmit.disabled =
          !newVariant.available;

        this._updatePlanAvailability();
        this._updateAllPlanPrices();
        this._updateOnetimePrice();
        this._updateUrl();

        document.dispatchEvent(
          new CustomEvent(
            'variant:change',
            {
              detail: {
                variant: newVariant
              }
            }
          )
        );

        this._updateGalleryImage();
        this._updateGiftCallout();
      }
    }

    _variantForPlanCard(
      card
    ) {
      const sellingPlanId =
        Number(
          card.dataset
            .sellingPlanId
        );

      const packSize =
        card.dataset.packSize;

      const supportsPlan =
        v =>
          (
            v.selling_plans ||
            []
          ).some(
            p =>
              p.id ==
              sellingPlanId
          );

      const candidates = [];

      if (
        this.packSizeOptionKey &&
        packSize
      ) {
        candidates.push({
          ...this.currentOptions,
          [this.packSizeOptionKey]:
            packSize
        });
      }

      candidates.push({
        ...this.currentOptions
      });

      for (
        const options of candidates
      ) {
        const match =
          this.variants.find(
            v =>
              JSON.stringify(
                v.options
              ) ===
                JSON.stringify(
                  options
                ) &&
              supportsPlan(v)
          );

        if (match) {
          return match;
        }
      }

      const sibling =
        this.variants.find(
          v =>
            Object.keys(
              this.currentOptions
            ).every(
              k =>
                k ===
                  this.packSizeOptionKey ||
                v.options[k] ===
                  this.currentOptions[k]
            ) &&
            supportsPlan(v)
        );

      return sibling || null;
    }

    _updatePlanAvailability() {
      if (this._syncingPlans) {
        return;
      }

      this._syncingPlans = true;

      let anyVisible = false;

      this.elPlanCards.forEach(
        card => {
          const variant =
            this._variantForPlanCard(
              card
            );

          const available =
            !!variant;

          card.classList.toggle(
            'hidden',
            !available
          );

          const input =
            card.querySelector(
              'input[name="subscription"]'
            );

          if (input) {
            input.disabled =
              !available;
          }

          if (available) {
            anyVisible = true;
          }
        }
      );

      const checked =
        this.elForm.querySelector(
          'input[name="subscription"]:checked'
        );

      if (
        this.hasSubscription &&
        (!checked ||
          checked.disabled)
      ) {
        const visibleInput =
          id =>
            [
              ...this.elPlanCards
            ]
              .filter(
                card =>
                  !card.classList.contains(
                    'hidden'
                  )
              )
              .filter(
                card =>
                  id == null ||
                  card.dataset
                    .sellingPlanId ==
                    id
              )
              .map(
                card =>
                  card.querySelector(
                    'input[name="subscription"]'
                  )
              )
              .find(Boolean);

        const keepCurrent =
          this.currentSubscription
            ? visibleInput(
                this.currentSubscription
                  .id
              )
            : null;

        if (keepCurrent) {
          keepCurrent.checked =
            true;
        } else {
          const fallback =
            visibleInput(null);

          if (fallback) {
            fallback.checked =
              true;

            this._syncingPlans =
              false;

            this._setSubscription(
              fallback.value
            );

            this._syncingPlans =
              true;
          }
        }
      }

      const plansWrap =
        this.querySelector(
          '[data-plan-cards]'
        );

      if (plansWrap) {
        plansWrap.classList.toggle(
          'hidden',
          !anyVisible
        );
      }

      this._syncingPlans =
        false;
    }

    _updateAllPlanPrices() {
      this.elPlanCards.forEach(
        card => {
          const packSize =
            card.dataset.packSize;

          if (!packSize) {
            return;
          }

          const variant =
            this._variantForPlanCard(
              card
            );

          if (!variant) {
            return;
          }

          const sellingPlanId =
            card.dataset
              .sellingPlanId;

          const allocation =
            variant.selling_plans?.find(
              p =>
                p.id ==
                Number(
                  sellingPlanId
                )
            );

          if (!allocation) {
            return;
          }

          const priceEl =
            card.querySelector(
              '[data-subscription-price]'
            );

          const capEl =
            card.querySelector(
              '[data-subscription-cap]'
            );

          if (priceEl) {
            priceEl.innerHTML =
              formatMoney(
                allocation.price,
                0
              );
          }

          if (capEl) {
            if (
              allocation.cap &&
              allocation.cap >
                allocation.price
            ) {
              capEl.innerHTML =
                formatMoney(
                  allocation.cap,
                  0
                );

              capEl.style.display =
                '';
            } else {
              capEl.style.display =
                'none';
            }
          }

          const perServingEl =
            card.querySelector(
              '[data-per-serving]'
            );

          if (perServingEl) {
            const servingsPerUnit =
              this._getServingsPerUnit();

            const resolvedPack =
              this.packSizeOptionKey
                ? variant.options[
                    this.packSizeOptionKey
                  ]
                : packSize;

            const multiplier =
              resolvedPack ===
              'Triple Pack'
                ? 3
                : resolvedPack ===
                  'Double Pack'
                ? 2
                : 1;

            const totalServings =
              servingsPerUnit *
              multiplier;

            const costPerServing =
              (
                allocation.price /
                100 /
                totalServings
              ).toFixed(2);

            perServingEl.textContent =
              `($${costPerServing}/serving)`;
          }
        }
      );
    }

    _getServingsPerUnit() {
      return this.servingsPerUnit;
    }

    async _fetchSection(
      variantId
    ) {
      const sectionId =
        this.dataset.sectionId;

      if (!sectionId) {
        return;
      }

      if (
        !window.location.pathname.includes(
          '/products/'
        )
      ) {
        return;
      }

      if (this._fetchAbort) {
        this._fetchAbort.abort();
      }

      this._fetchAbort =
        new AbortController();

      try {
        const resp =
          await fetch(
            `${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`,
            {
              signal:
                this._fetchAbort
                  .signal
            }
          );

        if (!resp.ok) {
          return;
        }

        const html =
          await resp.text();

        const doc =
          new DOMParser()
            .parseFromString(
              html,
              'text/html'
            );

        const newCards =
          doc.querySelector(
            '[data-plan-cards]'
          );

        const curCards =
          this.querySelector(
            '[data-plan-cards]'
          );

        if (
          newCards &&
          curCards
        ) {
          const selectedPlanId =
            this.elSellingPlanInput
              ?.value;

          curCards.innerHTML =
            newCards.innerHTML;

          this.elPlanCards =
            this.querySelectorAll(
              '[data-plan-card]'
            );

          if (selectedPlanId) {
            const input =
              this.querySelector(
                `input[data-selling-plan-id="${selectedPlanId}"]`
              );

            if (input) {
              input.checked =
                true;
            } else {
              const firstInput =
                this.elForm.querySelector(
                  'input[name="subscription"]'
                );

              if (firstInput) {
                firstInput.checked =
                  true;

                this._setSubscription(
                  firstInput.value
                );
              }
            }
          }

          this._updatePlanAvailability();
          this._updateSelectedPlanVisuals();
        }

        this._updateOnetimePrice();

        const newIngredients =
          doc.querySelector(
            '[data-ingredients]'
          );

        const curIngredients =
          this.querySelector(
            '[data-ingredients]'
          );

        if (
          newIngredients &&
          curIngredients
        ) {
          curIngredients.innerHTML =
            newIngredients.innerHTML;
        }
      } catch (e) {
        if (
          e.name ===
          'AbortError'
        ) {
          return;
        }

        console.warn(
          '[MP] Section refresh failed:',
          e
        );
      }
    }

    _updateOnetimePrice() {
      const singlePackVariant =
        this._getSinglePackVariant();

      const wrap =
        this.elBuyOnce
          ? this.elBuyOnce.parentElement
          : null;

      if (wrap) {
        wrap.classList.toggle(
          'hidden',
          !singlePackVariant
        );
      }

      if (
        !singlePackVariant ||
        !this.elOnetimePrice
      ) {
        return;
      }

      this.elOnetimePrice.innerHTML =
        formatMoney(
          singlePackVariant.price,
          0
        );
    }

    _getSinglePackVariant() {
      const oneTimeOk =
        v =>
          v &&
          !v.subscription_only &&
          v.available;

      if (
        !this.packSizeOptionKey
      ) {
        return oneTimeOk(
          this.currentVariant
        )
          ? this.currentVariant
          : null;
      }

      const opts = {
        ...this.currentOptions
      };

      opts[
        this.packSizeOptionKey
      ] = 'Single Pack';

      const single =
        this.variants.find(
          v =>
            JSON.stringify(
              v.options
            ) ===
            JSON.stringify(opts)
        );

      if (oneTimeOk(single)) {
        return single;
      }

      return oneTimeOk(
        this.currentVariant
      )
        ? this.currentVariant
        : null;
    }

    _updateUrl() {
      const url =
        new URL(
          window.location.href
        );

      url.searchParams.set(
        'variant',
        this.currentVariant.id
      );

      if (
        this.hasSubscription &&
        this.currentSubscription
      ) {
        url.searchParams.set(
          'selling_plan',
          this.currentSubscription.id
        );
      } else {
        url.searchParams.delete(
          'selling_plan'
        );
      }

      window.history.replaceState(
        {},
        '',
        url
      );
    }

    _scrollToHashTarget() {
      const hash =
        window.location.hash;

      if (!hash) return;

      const target =
        document.querySelector(
          hash
        );

      if (!target) return;

      requestAnimationFrame(
        () => {
          target.scrollIntoView({
            behavior: 'instant'
          });
        }
      );
    }

    _updateSubmitButton() {
      if (this.elSubmitCopy) {
        this.elSubmitCopy.innerHTML =
          this.currentVariant.available
            ? 'Buy Now'
            : 'Sold Out';
      }

      if (this.elDynamicPrice) {
        this.elDynamicPrice.innerHTML =
          `- ${formatMoney(
            this.currentVariant.price,
            0
          )}`;
      }
    }

    _updateSelectedPlanVisuals() {
      this.elPlanCards.forEach(
        card => {
          const input =
            card.querySelector(
              'input[name="subscription"]'
            );

          const radioDot =
            card.querySelector(
              '[data-plan-radio-dot]'
            );

          const radioRing =
            card.querySelector(
              '[data-plan-radio-ring]'
            );

          if (
            input &&
            input.checked
          ) {
            if (radioDot) {
              radioDot.style.transform =
                'scale(1)';
            }

            if (radioRing) {
              radioRing.style.borderColor =
                '#BE4300';

              radioRing.style.backgroundColor =
                '#BE4300';
            }
          } else {
            if (radioDot) {
              radioDot.style.transform =
                'scale(0)';
            }

            if (radioRing) {
              radioRing.style.borderColor =
                '#ADADAD';

              radioRing.style.backgroundColor =
                'white';
            }
          }
        }
      );
    }

    _preselectBestPlan() {
      let bestPlanInput = null;
      let firstPlanInput = null;

      this.elPlanCards.forEach(
        card => {
          const input =
            card.querySelector(
              'input[name="subscription"]'
            );

          if (!input) return;

          if (!firstPlanInput) {
            firstPlanInput =
              input;
          }

          if (
            card.dataset.packSize ===
            'Triple Pack'
          ) {
            bestPlanInput =
              input;
          }
        }
      );

      const toSelect =
        bestPlanInput ||
        firstPlanInput;

      if (toSelect) {
        toSelect.checked =
          true;

        this._setSubscription(
          toSelect.value
        );
      }
    }
    
    _buyOnce() {
      const singlePackVariant =
        this._getSinglePackVariant();

      if (
        !singlePackVariant ||
        !singlePackVariant.available
      ) {
        return;
      }

      if (
        this._isAddingToCart
      ) {
        return;
      }

      this._isAddingToCart =
        true;

      const originalHTML =
        this.elBuyOnce.innerHTML;

      this.elBuyOnce.innerHTML =
        'Adding to cart...';

      this.elBuyOnce.disabled =
        true;

      const addon =
        this._addonPayload();

      const giftVariantId =
        this._getCurrentGiftVariantId();

      const currentFlavor =
        this._getCurrentFlavor();

      const items = [];

      /*
       * ADDON
       */
      if (addon?.id) {
        items.push({
          id: Number(addon.id),
          quantity:
            Number(
              addon.quantity || 1
            )
        });
      }

      /*
       * SAMPLER
       */
      items.push({
        id: Number(
          singlePackVariant.id
        ),
        quantity: 1
      });

      /*
       * FREE GIFT
       */
      if (giftVariantId) {
        items.push({
          id: giftVariantId,
          quantity: 1,
          properties: {
            _gift:
              'sampler-free-gift',
            _gift_flavor:
              currentFlavor || ''
          }
        });
      }

      console.log(
        '[MP] BUY ONCE ITEMS:',
        items
      );

      

      this._addItemsToCart(items)
        .then(() => {
          this._isAddingToCart =
            false;

          this.elBuyOnce.disabled =
            false;

          this.elBuyOnce.innerHTML =
            originalHTML;
        })
        .catch(error => {
          console.error(
            '[MP] Buy Once cart add failed:',
            error
          );

          this._isAddingToCart =
            false;

          this.elBuyOnce.disabled =
            false;

          this.elBuyOnce.innerHTML =
            originalHTML;
        });
    }

    /*
     * =========================================================
     * ADDON
     * =========================================================
     */

    _updateAddonVisibility() {
      if (!this.elAddon) {
        return;
      }

      const exclude =
        this.elAddon.dataset
          .addonExcludeFlavor;

      const hide =
        !!exclude &&
        Object.values(
          this.currentOptions
        ).includes(exclude);

      this.elAddon.classList.toggle(
        'hidden',
        hide
      );

      if (
        hide &&
        this.elAddonInput
      ) {
        this.elAddonInput.checked =
          false;
      }
    }

    _addonPayload() {
      if (
        !this.elAddon ||
        !this.elAddonInput
      ) {
        return null;
      }

      if (
        this.elAddon.classList.contains(
          'hidden'
        ) ||
        !this.elAddonInput.checked
      ) {
        return null;
      }

      const id =
        parseInt(
          this.elAddon.dataset
            .addonVariantId,
          10
        );

      return id
        ? {
            id,
            quantity: 1
          }
        : null;
    }

    /*
     * =========================================================
     * MAIN ADD TO CART
     * =========================================================
     */

    _addToCart(ev) {
      ev.preventDefault();

      if (
        this._isAddingToCart
      ) {
        return;
      }

      if (
        !this.initialized ||
        !this.currentVariant ||
        !this.currentVariant.available
      ) {
        console.warn(
          '[MP] Cannot add product to cart.'
        );

        return;
      }

      this._isAddingToCart =
        true;

      /*
       * ---------------------------------------------------------
       * SAMPLER
       * ---------------------------------------------------------
       */

      const samplerItem = {
        id: Number(
          this.currentVariant.id
        ),
        quantity: 1
      };

      if (
        this.hasSubscription &&
        this.currentSubscription
      ) {
        samplerItem.selling_plan =
          Number(
            this.currentSubscription.id
          );
      }

      /*
       * ---------------------------------------------------------
       * CURRENT FLAVOR
       * ---------------------------------------------------------
       */

      const currentFlavor =
        this._getCurrentFlavor();

      /*
       * ---------------------------------------------------------
       * FREE GIFT
       * ---------------------------------------------------------
       */

      const gift =
        this._getCurrentGift();

      const giftVariantId =
        this._getCurrentGiftVariantId();

      /*
       * ---------------------------------------------------------
       * ADDON
       * ---------------------------------------------------------
       */

      const addon =
        this._addonPayload();

      const items = [];

      /*
       * ADDON FIRST
       */
      if (addon?.id) {
        items.push({
          id: Number(addon.id),
          quantity:
            Number(
              addon.quantity || 1
            )
        });
      }

      /*
       * SAMPLER
       */
      items.push(
        samplerItem
      );

      /*
       * FREE GIFT
       */
      if (giftVariantId) {
        items.push({
          id: giftVariantId,
          quantity: 1,
          properties: {
            _gift:
              'sampler-free-gift',
            _gift_flavor:
              currentFlavor || ''
          }
        });
      }

      console.group(
        '[MP] ADD TO CART'
      );

      console.log(
        '[MP] Sampler variant:',
        this.currentVariant.id
      );

      console.log(
        '[MP] Sampler price:',
        this.currentVariant.price
      );

      console.log(
        '[MP] Current flavor:',
        currentFlavor
      );

      console.log(
        '[MP] Gift:',
        gift
      );

      console.log(
        '[MP] Gift variant ID:',
        giftVariantId
      );

      console.log(
        '[MP] Addon:',
        addon
      );

      console.log(
        '[MP] Final cart items:',
        items
      );

      console.groupEnd();

      /*
       * BUTTON STATE
       */

      if (this.elSubmit) {
        this.elSubmit.disabled =
          true;
      }

      if (this.elSubmitCopy) {
        this.elSubmitCopy.innerHTML =
          'Buy Now';
      }

      if (this.elSubmitIcon) {
        this.elSubmitIcon.classList.add(
          'hidden'
        );
      }

      /*
       * ADD EVERYTHING
       */

      this._addItemsToCart(
        items
      )
        .then(
          cartResponse => {
            console.log(
              '[MP] Cart successfully updated:',
              cartResponse
            );

            this._isAddingToCart =
              false;

            if (this.elSubmit) {
              this.elSubmit.disabled =
                false;
            }

            if (this.elSubmitIcon) {
              this.elSubmitIcon.classList.remove(
                'hidden'
              );
            }

            this._updateSubmitButton();
          }
        )
        .catch(
          error => {
            console.error(
              '[MP] Add to cart failed:',
              error
            );

            this._isAddingToCart =
              false;

            if (this.elSubmit) {
              this.elSubmit.disabled =
                false;
            }

            if (this.elSubmitIcon) {
              this.elSubmitIcon.classList.remove(
                'hidden'
              );
            }

            if (this.elSubmitCopy) {
              this.elSubmitCopy.innerHTML =
                'Buy Now';
            }

            this._updateSubmitButton();
          }
        );
    }

    _isGiftAlreadyInCart() {
      return fetch(
        `${window.Shopify.routes.root}cart.js`
      )
        .then(response => {
          if (!response.ok) {
            throw new Error('Could not fetch cart.');
          }

          return response.json();
        })
        .then(cart => {
          return cart.items.some(item => {
            return (
              item.properties &&
              item.properties._gift ===
                'sampler-free-gift'
            );
          });
        })
        .catch(error => {
          console.warn(
            '[MP] Could not check existing gift:',
            error
          );

          return false;
        });
    }

    // _addItemsToCart(items) {
    //   if (
    //     !Array.isArray(items) ||
    //     !items.length
    //   ) {
    //     return Promise.reject(
    //       new Error(
    //         'No items to add to cart.'
    //       )
    //     );
    //   }

    //   if (
    //     typeof window.addToCart !==
    //     'function'
    //   ) {
    //     return Promise.reject(
    //       new Error(
    //         'Cart system is not loaded.'
    //       )
    //     );
    //   }

    //   console.log(
    //     '[MP] ITEMS BEFORE GIFT CHECK:',
    //     items
    //   );

    //   /*
    //   * ---------------------------------------------------------
    //   * CHECK IF FREE GIFT IS ALREADY IN CART
    //   * ---------------------------------------------------------
    //   */

    //   return this._isGiftAlreadyInCart()
    //     .then(
    //       giftAlreadyInCart => {
    //         if (giftAlreadyInCart) {
    //           console.log(
    //             '[MP] FREE GIFT ALREADY IN CART — REMOVING NEW GIFT'
    //           );

    //           items =
    //             items.filter(
    //               item =>
    //                 !(
    //                   item.properties &&
    //                   item.properties._gift ===
    //                     'sampler-free-gift'
    //                 )
    //             );
    //         }

    //         console.log(
    //           '[MP] FINAL ITEMS TO ADD:',
    //           items
    //         );

    //         /*
    //         * If only the gift was left and it was
    //         * already in cart, don't call addToCart.
    //         */

    //         if (!items.length) {
    //           return null;
    //         }

    //         /*
    //         * -----------------------------------------------------
    //         * TEMPORARILY DISABLE GWP
    //         * -----------------------------------------------------
    //         */

    //         const originalGwpOffers =
    //           window.gwpOffers;

    //         window.gwpOffers = [];

    //         return window
    //           .addToCart({
    //             items: items
    //           })
    //           .then(
    //             response => {
    //               window.gwpOffers =
    //                 originalGwpOffers;

    //               console.log(
    //                 '[MP] CART UPDATED:',
    //                 response
    //               );

    //               return response;
    //             }
    //           )
    //           .catch(error => {
    //             window.gwpOffers =
    //               originalGwpOffers;

    //             throw error;
    //           });
    //       }
    //     );
    // }

    _addItemsToCart(items) {
  if (!Array.isArray(items) || !items.length) {
    return Promise.reject(
      new Error('No items to add to cart.')
    );
  }

  const originalGwpOffers = window.gwpOffers;
  window.gwpOffers = [];

  const discountCode =
    document.querySelector('[data-submit-btn]')?.dataset.discount;

  // Get free gift variant ID from Liquid hidden input
  const giftVariantId = document.querySelector(
    '[data-gift-variant-id]'
  )?.value;

  return this._isGiftAlreadyInCart()
    .then((giftAlreadyInCart) => {
      let finalItems = [...items];

      // Remove duplicate gift if already in cart
      if (giftAlreadyInCart) {
        finalItems = finalItems.filter(
          item =>
            !(
              item.properties &&
              item.properties._gift === 'sampler-free-gift'
            )
        );
      } else if (giftVariantId) {
        // Add free gift to cart
        finalItems.push({
          id: Number(giftVariantId),
          quantity: 1,
          properties: {
            _gift: 'sampler-free-gift'
          }
        });
      }

      if (!finalItems.length) {
        throw new Error('No items to add.');
      }

      const root = window.Shopify?.routes?.root || '/';

      return fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: finalItems
        })
      }).then(async (response) => {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.description ||
            data.message ||
            'Unable to add items to cart.'
          );
        }

        return data;
      });
    })
    .then((response) => {
      const root = window.Shopify?.routes?.root || '/';

      if (discountCode) {
        window.location.assign(
          `${root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(root + 'checkout')}`
        );
      } else {
        window.location.assign(`${root}checkout`);
      }

      return response;
    })
    .finally(() => {
      window.gwpOffers = originalGwpOffers;
    });
}

//     _addItemsToCart(items) {
//   if (!Array.isArray(items) || !items.length) {
//     return Promise.reject(
//       new Error('No items to add to cart.')
//     );
//   }

//   const originalGwpOffers = window.gwpOffers;
//   window.gwpOffers = [];

//   // Get discount code from Buy Now button
//   const discountCode =
//     document.querySelector('[data-submit-btn]')?.dataset.discount;

//   return this._isGiftAlreadyInCart()
//     .then((giftAlreadyInCart) => {
//       let finalItems = [...items];

//       if (giftAlreadyInCart) {
//         finalItems = finalItems.filter(
//           item =>
//             !(
//               item.properties &&
//               item.properties._gift === 'sampler-free-gift'
//             )
//         );
//       }

//       if (!finalItems.length) {
//         throw new Error('No items to add.');
//       }

//       const root = window.Shopify?.routes?.root || '/';

//       return fetch(`${root}cart/add.js`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json'
//         },
//         body: JSON.stringify({
//           items: finalItems
//         })
//       }).then(async (response) => {
//         const data = await response.json();

//         if (!response.ok) {
//           throw new Error(
//             data.description ||
//             data.message ||
//             'Unable to add items to cart.'
//           );
//         }

//         return data;
//       });
//     })
//     .then((response) => {
//       const root = window.Shopify?.routes?.root || '/';

//       // Apply discount and redirect to checkout
//       if (discountCode) {
//         window.location.assign(
//           `${root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(root + 'checkout')}`
//         );
//       } else {
//         window.location.assign(`${root}checkout`);
//       }

//       return response;
//     })
//     .finally(() => {
//       window.gwpOffers = originalGwpOffers;
//     });
// }

//     _addItemsToCart(items) {
//   if (!Array.isArray(items) || !items.length) {
//     return Promise.reject(
//       new Error('No items to add to cart.')
//     );
//   }

//   const originalGwpOffers = window.gwpOffers;
//   window.gwpOffers = [];

//   return this._isGiftAlreadyInCart()
//     .then((giftAlreadyInCart) => {
//       let finalItems = [...items];

//       if (giftAlreadyInCart) {
//         finalItems = finalItems.filter(
//           item =>
//             !(
//               item.properties &&
//               item.properties._gift === 'sampler-free-gift'
//             )
//         );
//       }

//       if (!finalItems.length) {
//         throw new Error('No items to add.');
//       }

//       const root = window.Shopify?.routes?.root || '/';

//       return fetch(`${root}cart/add.js`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json'
//         },
//         body: JSON.stringify({
//           items: finalItems
//         })
//       }).then(async (response) => {
//         const data = await response.json();

//         if (!response.ok) {
//           throw new Error(
//             data.description || data.message ||
//             'Unable to add items to cart.'
//           );
//         }

//         return data;
//       });
//     })
//     .then((response) => {
//       window.location.assign(
//         `${window.Shopify?.routes?.root || '/'}checkout?view=`
//       );

//       return response;
//     })
//     .finally(() => {
//       window.gwpOffers = originalGwpOffers;
//     });
// }

    /*
     * =========================================================
     * DOSING GUIDE
     * =========================================================
     */

    _initDosingGuide() {
      var self = this;

      this._dgModal =
        this.querySelector(
          '[data-dosing-modal]'
        );

      if (!this._dgModal) {
        return;
      }

      document.body.appendChild(
        this._dgModal
      );

      this._dgCard =
        this._dgModal.querySelector(
          '[data-dosing-card]'
        );

      this._dgBackdrop =
        this._dgModal.querySelector(
          '[data-dosing-backdrop]'
        );

      this._dgOpen = false;

      var trigger =
        this.querySelector(
          '[data-dosing-guide]'
        );

      if (trigger) {
        trigger.addEventListener(
          'click',
          function () {
            self._openDosingGuide();
          }
        );
      }

      this._dgModal
        .querySelector(
          '[data-dosing-close]'
        )
        .addEventListener(
          'click',
          function () {
            self._closeDosingGuide();
          }
        );

      this._dgBackdrop.addEventListener(
        'click',
        function (e) {
          if (
            e.target ===
            self._dgBackdrop
          ) {
            self._closeDosingGuide();
          }
        }
      );

      document.addEventListener(
        'keydown',
        function (e) {
          if (!self._dgOpen) {
            return;
          }

          if (
            e.key === 'Escape'
          ) {
            self._closeDosingGuide();
          }
        }
      );
    }

    _openDosingGuide() {
      this._dgModal.classList.remove(
        'hidden'
      );

      this._dgOpen = true;

      document.body.style.overflow =
        'hidden';

      this._dgModal.animate(
        [
          { opacity: 0 },
          { opacity: 1 }
        ],
        {
          duration: 250,
          easing: 'ease-out',
          fill: 'forwards'
        }
      );

      this._dgCard.animate(
        [
          {
            opacity: 0,
            transform:
              'scale(0.95) translateY(20px)'
          },
          {
            opacity: 1,
            transform:
              'scale(1) translateY(0)'
          }
        ],
        {
          duration: 300,
          easing: 'ease-out',
          fill: 'forwards'
        }
      );
    }

    _closeDosingGuide() {
      var self = this;

      var backdropAnim =
        this._dgModal.animate(
          [
            { opacity: 1 },
            { opacity: 0 }
          ],
          {
            duration: 200,
            easing: 'ease-in',
            fill: 'forwards'
          }
        );

      this._dgCard.animate(
        [
          {
            opacity: 1,
            transform:
              'scale(1) translateY(0)'
          },
          {
            opacity: 0,
            transform:
              'scale(0.95) translateY(20px)'
          }
        ],
        {
          duration: 200,
          easing: 'ease-in',
          fill: 'forwards'
        }
      );

      backdropAnim.onfinish =
        function () {
          self._dgModal.classList.add(
            'hidden'
          );

          self._dgOpen = false;

          document.body.style.overflow =
            '';
        };
    }

    /*
     * =========================================================
     * LIGHTBOX
     * =========================================================
     */

    _initLightbox() {
      var self = this;

      this._lbModal =
        this.querySelector(
          '[data-lightbox-modal]'
        );

      if (!this._lbModal) {
        return;
      }

      document.body.appendChild(
        this._lbModal
      );

      this._lbImage =
        this._lbModal.querySelector(
          '[data-lightbox-image]'
        );

      this._lbVideo =
        this._lbModal.querySelector(
          '[data-lightbox-video-el]'
        );

      this._lbCard =
        this._lbModal.querySelector(
          '[data-lightbox-card]'
        );

      this._lbBackdrop =
        this._lbModal.querySelector(
          '[data-lightbox-backdrop]'
        );

      this._lbDotsContainer =
        this._lbModal.querySelector(
          '[data-lightbox-dots]'
        );

      this._lbOpen = false;
      this._lbIndex = 0;

      var allTriggers = [
        ...this.querySelectorAll(
          '[data-lightbox-trigger]'
        )
      ];

      if (!allTriggers.length) {
        return;
      }

      var mobileTrack =
        this.querySelector(
          '[data-carousel-track]'
        );

      var desktopGrid =
        this.querySelector(
          '.lg\\:grid'
        );

      this._lbMobileItems =
        mobileTrack
          ? [
              ...mobileTrack.querySelectorAll(
                '[data-lightbox-trigger]'
              )
            ]
          : [];

      this._lbDesktopItems =
        desktopGrid
          ? [
              ...desktopGrid.querySelectorAll(
                '[data-lightbox-trigger]'
              )
            ]
          : [];

      this._lbItems =
        window.innerWidth >= 1024
          ? this._lbDesktopItems
          : this._lbMobileItems;

      if (
        !this._lbItems.length
      ) {
        this._lbItems =
          allTriggers;
      }

      allTriggers.forEach(
        item => {
          item.addEventListener(
            'click',
            e => {
              if (
                e.target.closest(
                  '[data-video-controls]'
                )
              ) {
                return;
              }

              var items =
                window.innerWidth >=
                1024
                  ? self._lbDesktopItems
                  : self._lbMobileItems;

              if (!items.length) {
                items =
                  allTriggers;
              }

              self._lbItems =
                items;

              var idx =
                items.indexOf(
                  item
                );

              if (idx === -1) {
                idx = 0;
              }

              self._buildLightboxDots();
              self._openLightbox(
                idx
              );
            }
          );
        }
      );

      this._lbModal
        .querySelector(
          '[data-lightbox-close]'
        )
        .addEventListener(
          'click',
          function () {
            self._closeLightbox();
          }
        );

      this._lbBackdrop.addEventListener(
        'click',
        function (e) {
          if (
            e.target ===
            self._lbBackdrop
          ) {
            self._closeLightbox();
          }
        }
      );

      this._lbModal
        .querySelector(
          '[data-lightbox-prev]'
        )
        .addEventListener(
          'click',
          function () {
            self._lbGo(-1);
          }
        );

      this._lbModal
        .querySelector(
          '[data-lightbox-next]'
        )
        .addEventListener(
          'click',
          function () {
            self._lbGo(1);
          }
        );

      document.addEventListener(
        'keydown',
        function (e) {
          if (!self._lbOpen) {
            return;
          }

          if (
            e.key === 'Escape'
          ) {
            self._closeLightbox();
          }

          if (
            e.key === 'ArrowLeft'
          ) {
            self._lbGo(-1);
          }

          if (
            e.key === 'ArrowRight'
          ) {
            self._lbGo(1);
          }
        }
      );

      this._buildLightboxDots();
    }

    _buildLightboxDots() {
      if (
        !this._lbDotsContainer
      ) {
        return;
      }

      this._lbDotsContainer.innerHTML =
        '';

      for (
        var i = 0;
        i < this._lbItems.length;
        i++
      ) {
        var dot =
          document.createElement(
            'button'
          );

        dot.type =
          'button';

        dot.className =
          'h-1 rounded-full transition-all';

        dot.style.width =
          i === 0
            ? '20px'
            : '4px';

        dot.style.backgroundColor =
          i === 0
            ? '#BE4300'
            : 'rgba(190, 67, 0, 0.4)';

        dot.dataset.lbDot =
          i;

        dot.ariaLabel =
          'Go to image ' +
          (i + 1);

        (function (
          idx,
          self
        ) {
          dot.addEventListener(
            'click',
            function () {
              self._lbGoTo(
                idx
              );
            }
          );
        })(i, this);

        this._lbDotsContainer.appendChild(
          dot
        );
      }

      this._lbDots = [
        ...this._lbDotsContainer.querySelectorAll(
          '[data-lb-dot]'
        )
      ];
    }

    _openLightbox(index) {
      this._lbIndex =
        index;

      this._lbSetImage(
        index
      );

      this._lbUpdateDots();

      this._lbModal.classList.remove(
        'hidden'
      );

      this._lbOpen = true;

      document.body.style.overflow =
        'hidden';

      this._lbModal.animate(
        [
          { opacity: 0 },
          { opacity: 1 }
        ],
        {
          duration: 250,
          easing: 'ease-out',
          fill: 'forwards'
        }
      );

      this._lbCard.animate(
        [
          {
            opacity: 0,
            transform:
              'scale(0.95) translateY(20px)'
          },
          {
            opacity: 1,
            transform:
              'scale(1) translateY(0)'
          }
        ],
        {
          duration: 300,
          easing: 'ease-out',
          fill: 'forwards'
        }
      );

      this._preloadLightboxImages(
        index
      );
    }

    _preloadLightboxImages(
      currentIndex
    ) {
      if (
        this._lbPreloaded
      ) {
        return;
      }

      this._lbPreloaded =
        true;

      var items =
        this._lbItems;

      for (
        var i = 0;
        i < items.length;
        i++
      ) {
        if (
          i === currentIndex
        ) {
          continue;
        }

        var item =
          items[i];

        if (
          item.dataset
            .lightboxType ===
          'video'
        ) {
          continue;
        }

        var src =
          item.dataset
            .lightboxSrc;

        if (src) {
          var img =
            new Image();

          img.src =
            src;
        }
      }
    }

    _closeLightbox() {
      var self = this;

      if (this._lbVideo) {
        this._lbVideo.pause();
      }

      var backdropAnim =
        this._lbModal.animate(
          [
            { opacity: 1 },
            { opacity: 0 }
          ],
          {
            duration: 200,
            easing: 'ease-in',
            fill: 'forwards'
          }
        );

      this._lbCard.animate(
        [
          {
            opacity: 1,
            transform:
              'scale(1) translateY(0)'
          },
          {
            opacity: 0,
            transform:
              'scale(0.95) translateY(20px)'
          }
        ],
        {
          duration: 200,
          easing: 'ease-in',
          fill: 'forwards'
        }
      );

      backdropAnim.onfinish =
        function () {
          self._lbModal.classList.add(
            'hidden'
          );

          self._lbOpen = false;

          document.body.style.overflow =
            '';
        };
    }

    _lbGo(dir) {
      var next =
        (
          (
            this._lbIndex +
            dir
          ) %
            this._lbItems.length +
          this._lbItems.length
        ) %
        this._lbItems.length;

      this._lbGoTo(
        next
      );
    }

    _lbGoTo(index) {
      if (
        index ===
        this._lbIndex
      ) {
        return;
      }

      this._lbIndex =
        index;

      this._lbSetImage(
        index
      );

      this._lbUpdateDots();
    }

    _lbSetImage(index) {
      var item =
        this._lbItems[
          index
        ];

      if (!item) {
        return;
      }

      var isVideo =
        item.dataset
          .lightboxType ===
        'video';

      if (isVideo) {
        this._lbImage.classList.add(
          'hidden'
        );

        this._lbVideo.classList.remove(
          'hidden'
        );

        var videoSrc =
          item.dataset
            .lightboxVideo;

        if (
          this._lbVideo.querySelector(
            'source'
          )?.src !==
          videoSrc
        ) {
          this._lbVideo.innerHTML =
            '<source src="' +
            videoSrc +
            '" type="video/mp4">';

          this._lbVideo.load();
        }

        this._lbVideo
          .play()
          .catch(
            function () {}
          );
      } else {
        this._lbVideo.classList.add(
          'hidden'
        );

        this._lbVideo.pause();

        this._lbImage.classList.remove(
          'hidden'
        );

        this._lbImage.style.opacity =
          '0';

        var newSrc =
          item.dataset
            .lightboxSrc;

        this._lbImage.alt =
          item.querySelector(
            'img'
          )?.alt || '';

        var img =
          this._lbImage;

        var show =
          function () {
            img.style.transition =
              'opacity 150ms ease';

            img.style.opacity =
              '1';
          };

        if (
          img.src ===
            newSrc &&
          img.complete
        ) {
          show();
        } else {
          img.onload =
            function () {
              show();
            };

          img.src =
            newSrc;
        }
      }
    }

    _lbUpdateDots() {
      if (!this._lbDots) {
        return;
      }

      this._lbDots.forEach(
        function (
          dot,
          i
        ) {
          var isActive =
            i ===
            this._lbIndex;

          dot.style.width =
            isActive
              ? '20px'
              : '4px';

          dot.style.backgroundColor =
            isActive
              ? '#BE4300'
              : 'rgba(190, 67, 0, 0.4)';
        }.bind(this)
      );
    }
  }

  customElements.define(
    'main-product',
    MainProduct
  );
}








/* review scroll */

document.querySelectorAll('.template-sampler-lp .review-block').forEach(function (element) {
  element.addEventListener('click', function (e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    var href = element.getAttribute('href');

    if (!href) return;

    // Remove # from href
    var targetId = href.replace(/^#/, '');

    // Find target element by ID
    var target = document.getElementById(targetId);

    if (!target) return;

    // Smooth scroll with 112px top offset
    var scrollPosition =
      target.getBoundingClientRect().top + window.pageYOffset - 80;

    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    });
  });
});